import hash from 'hash.js';
import * as z from 'zod';

import {
    IntegrationInstallationSiteSelection,
    IntegrationInstallationSpaceSelection,
    IntegrationInstallationStatus,
    IntegrationScope,
} from '@gitbook/api-client';

import type { ContentSharing } from '../content';
import { Timestamp } from '../db/firestore';
import { UserContributionsSchema } from '../helpers';
import { getIntegrationPublicURLBase } from './integrations';

/**
 * Specific configuration in the installation.
 * It can be used for the space / page / organization configuration.
 */
const InstallationConfigPrimitiveSchema = z.union([z.number(), z.string(), z.boolean(), z.null()]);

type InstallationConfigPrimitive = number | string | boolean | null;

/**
 * Value stored for an integration installation configuration.
 * Declared explicitly (rather than inferred) because the schema is recursive
 * via `z.lazy`, which the compiler cannot infer on its own.
 */
export interface IntegrationInstallationConfig {
    [key: string]:
        | InstallationConfigPrimitive
        | Array<InstallationConfigPrimitive | { [key: string]: IntegrationInstallationConfig }>
        | IntegrationInstallationConfig;
}

export const IntegrationInstallationConfigSchema: z.ZodType<IntegrationInstallationConfig> =
    z.record(
        z.string(),
        z.union([
            InstallationConfigPrimitiveSchema,
            z.array(
                z.union([
                    InstallationConfigPrimitiveSchema,
                    z.record(
                        z.string(),
                        z.lazy(() => IntegrationInstallationConfigSchema)
                    ),
                ])
            ),
            z.lazy(() => IntegrationInstallationConfigSchema),
        ])
    );

export const IntegrationInstallationTargetSchema = z.object({
    organization: z.string(),
});
export type IntegrationInstallationTarget = z.infer<typeof IntegrationInstallationTargetSchema>;

export { IntegrationInstallationSpaceSelection, IntegrationInstallationStatus };

/**
 * Installation of a specific integration on a target (user/organization)
 */
export const ExternalIdsSchema = z
    .array(z.string())
    .max(5)
    .refine((ids) => ids.join('').length <= 640, {
        error: 'External IDs too long',
    });

export const IntegrationInstallationSchema = z.object({
    /**
     * Unique key of the installation
     * See `getIntegrationInstallationKey`
     */
    key: z.string(),

    /** External IDs defined by the integration */
    externalIds: ExternalIdsSchema.optional(),

    /** Target of the installation */
    target: IntegrationInstallationTargetSchema,

    /**
     * @deprecated
     * We use scopes on the integration as the source of truth. This field is no
     * longer in use.
     */
    scopes: z.enum(IntegrationScope).array().optional(),

    /** Current status of the installation */
    status: z.enum(IntegrationInstallationStatus),

    /** Key of the integration. Useful when querying all installations for an owner */
    integration: z.string(),

    spaceSelection: z.enum(IntegrationInstallationSpaceSelection),

    siteSelection: z.enum(IntegrationInstallationSiteSelection).optional(),

    /** IDs of the spaces for which this integration is enabled */
    spaces: z.array(z.string()),

    /** Contributions to edit this installation */
    edits: UserContributionsSchema,

    configurations: z
        .object({
            account: z.union([IntegrationInstallationConfigSchema.optional(), z.undefined()]),
        })
        .optional(),

    /**
     * Whether the installation was created by the migration script.
     * Represents the ID of the user from which the installation was migrated.
     * @deprecated This field is no longer used in business logic but kept for schema compatibility
     */
    migrated: z.string().optional(),

    /**
     * Whether the installation was created by the migration script
     * that migrates from a previous integration.
     * @deprecated This field is no longer used in business logic but kept for schema compatibility
     */
    migratedFromIntegration: z.string().optional(),

    /**
     * Whether the user installation has been deprecated & should be ignored/removed
     * @deprecated This field is no longer used in business logic but kept for schema compatibility
     */
    deprecated: z.boolean().optional(),

    createdAt: z.instanceof(Timestamp).optional(),

    /** Whether the installation is proxied through the backend
     * Used for integrations
     */
    proxied: z.boolean().optional(),
});
export type IntegrationInstallation = z.infer<typeof IntegrationInstallationSchema>;

/**
 * Object stored on the space or collection representing the list of integrations installed.
 * It is used to easily know which integrations have been installed without listing the installations (which require specific permissions).
 *
 * Since an integration can be installed multiple times on a space, we store the count.
 * (The count is never used, so it's omitted in the new PlanetScale schema.)
 */
export const IntegrationInstallationMapSchema = z.record(z.string(), z.number());

/**
 * Get the installation key for a specific integration.
 */
export function getIntegrationInstallationKey(
    integrationKey: string,
    target: IntegrationInstallationTarget
): string {
    const id = `o:${target.organization}:i:${integrationKey}`;
    return hash.sha256().update(id).digest('hex');
}

/**
 * Get an integration target for a specific content.
 */
export function getIntegrationInstallationTarget(
    content: ContentSharing
): IntegrationInstallationTarget {
    return { organization: content.organization };
}

/**
 * Get the public URL for an integration's installation.
 */
export function getIntegrationInstallationPublicURL(
    installation: IntegrationInstallation | Pick<IntegrationInstallation, 'key' | 'integration'>
): string {
    return `${getIntegrationPublicURLBase(installation.integration)}/installations/${
        installation.key
    }/installation`;
}
